import { getAncestorNodeOfType } from '@/lib/zy-tiptap-utils';
import { Extension, isNodeEmpty } from '@tiptap/core';
import { Node as PMNode } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import type { ZyExampleType } from '../zyexample-extension/zyexample-extension';

const DEFAULT_DATA_ATTRIBUTE = 'placeholder';

/**
 * Prepares the placeholder attribute by ensuring it is properly formatted.
 * @param attr - The placeholder attribute string.
 * @returns The prepared placeholder attribute string.
 */
export function preparePlaceholderAttribute(attr: string): string {
    return (
        attr
            // replace whitespace with dashes
            .replace(/\s+/g, '-')
            // replace non-alphanumeric  characters
            // or special chars like $, %, &, etc.
            // but not dashes
            .replace(/[^a-zA-Z0-9-]/g, '')
            // and replace any numeric character at the start
            .replace(/^[0-9-]+/, '')
            // and finally replace any stray, leading dashes
            .replace(/^-+/, '')
            .toLowerCase()
    );
}

const placeholders: Record<string, string> = {
    detectAnswerQuestion: 'Question text',
    questionSet: 'Additional instructions (optional)',
    multipleChoiceQuestion: 'Question text',
    multipleChoiceChoice: 'Answer choice text',
    multipleChoiceExplanation: 'Explanation text',
    shortAnswerQuestion: 'Question text',
    shortAnswerAnswer: 'Answer choice text',
    shortAnswerHint: 'Hint text',
    shortAnswerExplanation: 'Explanation text',
    matchingPairTerm: 'Match term',
    matchingPairDefinition: 'Match definition',
    matchingPairExplanation: 'Match explanation',
    detailsContent: 'Summary',
    zySetup: 'Instructions',
    zyProblem: 'Problem text',
    zyProblemSolution: 'Solution text',
    zyStudioInstruction: 'Start typing... (optional)',
    zyClassicLabInstruction: 'Start typing... (optional)',
    zyDEInstruction: 'Instruction text... (optional)',
};

const zyExamplePlaceholders: Record<string, string> = {
    figure: 'Start typing...(optional)',
};

const TABLE_CELL_PARENT_TYPES = ['tableCell', 'tableHeader'];

/**
 * Resolves the placeholder text for an empty paragraph node based on its parent node type.
 * Only returns placeholder text for the first child of the parent; returns empty string otherwise.
 * @param doc - The ProseMirror document node.
 * @param pos - The absolute position of the empty paragraph within the document.
 * @returns The placeholder text to display, or an empty string if no placeholder applies.
 */
function getPlaceholderText(doc: PMNode, pos: number): string {
    const $pos = doc.resolve(pos);
    if ($pos.index() !== 0) {
        return '';
    }

    const parent = $pos.parent;
    if (!parent) {
        return '';
    }

    const defaultPlaceholder = 'Start typing...';

    if (parent.type.name === 'listItem') {
        const zyAsideParent = getAncestorNodeOfType($pos, 'zyAside');
        if (zyAsideParent) {
            return 'Objective';
        }

        const zyExploreParent = getAncestorNodeOfType($pos, 'zyExplore');
        if (zyExploreParent) {
            return 'Link';
        }

        return '';
    }

    if (TABLE_CELL_PARENT_TYPES.includes(parent.type.name)) {
        return '';
    }

    if (parent.type.name === 'zyExample') {
        return zyExamplePlaceholders[parent.attrs.type as ZyExampleType] ?? defaultPlaceholder;
    }
    return placeholders[parent.type.name] ?? defaultPlaceholder;
}

export interface ZyPlaceholderOptions {
    emptyEditorClass: string;
    emptyNodeClass: string;
    dataAttribute: string;
    showOnlyWhenEditable: boolean;
}

export const ZyPlaceholder = Extension.create<ZyPlaceholderOptions>({
    name: 'zyPlaceholder',

    addOptions() {
        return {
            emptyEditorClass: 'is-editor-empty',
            emptyNodeClass: 'is-empty',
            dataAttribute: DEFAULT_DATA_ATTRIBUTE,
            showOnlyWhenEditable: true,
        };
    },

    addProseMirrorPlugins() {
        const dataAttribute = this.options.dataAttribute
            ? `data-${preparePlaceholderAttribute(this.options.dataAttribute)}`
            : `data-${DEFAULT_DATA_ATTRIBUTE}`;

        return [
            new Plugin({
                key: new PluginKey('zyPlaceholder'),
                props: {
                    decorations: ({ doc }) => {
                        const active = this.editor.isEditable || !this.options.showOnlyWhenEditable;
                        if (!active) {
                            return null;
                        }

                        const isEmptyDoc = this.editor.isEmpty;
                        const decorations: Decoration[] = [];

                        doc.descendants((node, pos) => {
                            if (node.type.name !== 'paragraph' || !isNodeEmpty(node)) {
                                return;
                            }

                            const placeholderText = getPlaceholderText(doc, pos);
                            const classes = [this.options.emptyNodeClass];
                            if (isEmptyDoc) {
                                classes.push(this.options.emptyEditorClass);
                            }

                            decorations.push(
                                Decoration.node(pos, pos + node.nodeSize, {
                                    class: classes.join(' '),
                                    [dataAttribute]: placeholderText,
                                }),
                            );
                        });

                        return DecorationSet.create(doc, decorations);
                    },
                },
            }),
        ];
    },
});
